// "Add to home screen" prompt. docs/replan-v2.md R11.
//
// Captures the browser's `beforeinstallprompt` event (Chrome/Edge/Android),
// shows a dismissible brand banner, and fires the native install flow on tap.
// Dismissal is remembered in localStorage so we don't nag. Hidden entirely when
// already installed (display-mode: standalone) or on browsers that never fire
// the event (iOS Safari — which installs via the share sheet instead).

import { useT } from '@fajrarabi/i18n/react';
import { Button } from '@fajrarabi/ui';
import { useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_KEY = 'fa_pwa_install_dismissed';

export function InstallPrompt() {
  const t = useT();
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.matchMedia('(display-mode: standalone)').matches) return; // already installed
    if (localStorage.getItem(DISMISS_KEY)) return;

    const onPrompt = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
      setVisible(true);
    };
    const onInstalled = () => setVisible(false);
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  if (!visible || !deferred) return null;

  const dismiss = () => {
    setVisible(false);
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // ignore quota/private-mode
    }
  };

  const install = async () => {
    try {
      await deferred.prompt();
      await deferred.userChoice;
    } finally {
      setVisible(false);
      setDeferred(null);
    }
  };

  return (
    <div className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-md rounded-2xl bg-white p-4 shadow-[0_24px_60px_-24px_rgba(31,26,23,0.45)] ring-1 ring-burgundy-500/10">
      <div className="flex items-center gap-3">
        <img src="/icons/icon-192.png" alt="" className="h-12 w-12 rounded-xl" />
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-ink-900">{t('app.pwa.install_title')}</p>
          <p className="text-sm text-ink-500">{t('app.pwa.install_body')}</p>
        </div>
      </div>
      <div className="mt-3 flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={dismiss}>
          {t('app.pwa.install_dismiss')}
        </Button>
        <Button size="sm" onClick={() => void install()}>
          {t('app.pwa.install_cta')}
        </Button>
      </div>
    </div>
  );
}
